import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "src/utils/axios";
import {
  addFailureSample,
  addHeartbeatSample,
  clearHeartbeatSamples,
} from "src/redux/slices/networkStatus";
import { getDeviceId } from "src/utils/deviceId";

// Cada 60 segundos consolidamos los samples de latencia y los fallos
// acumulados desde el último flush y los enviamos al backend agrupados
// en el bucket horario actual. Bajamos de 5 min a 60s para que las
// degradaciones / caídas se vean reflejadas en el chart casi al
// instante (antes había hasta 5 min de retraso). El bucket sigue
// siendo horario (el backend hace upsert merge ponderado), así que
// no se multiplica el número de filas en BD.
const FLUSH_INTERVAL_MS = 60 * 1000;

// Heartbeat: cada 60s hacemos un GET /connectivity/ping para medir la
// latencia REAL aunque el TPV no tenga actividad. Sin esto, los samples
// solo aparecen cuando hay tráfico HTTP natural → un restaurante parado
// 30 min queda sin medir y no detectamos variaciones / caídas.
//
// 60s da 5 puntos por bucket de 5min y 60 por hora — suficiente para
// reaccionar a degradaciones de la conexión sin spamear el backend.
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
// Timeout del propio ping. Si el servidor no responde en 5s lo damos
// por failure (sample alto + addFailureSample) — es el mismo umbral
// que usaría un usuario para considerar "lento de cojones".
const HEARTBEAT_TIMEOUT_MS = 5000;
// Cuando el ping falla por timeout / red, registramos un sample con
// este valor para que el p95/max del bucket lo reflejen visualmente
// en el chart. Coincide con el timeout para coherencia.
const HEARTBEAT_FAIL_SAMPLE_MS = HEARTBEAT_TIMEOUT_MS;

function percentile(sorted, p) {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

// Bucket de 5 minutos — el backend trunca al mismo paso. Mantener este
// número en sync con `BucketMinutes` en `ConnectivityService.RecordSampleAsync`.
// Granularidad fina permite ver picos a las 11:15, 11:30, 11:45 en el chart.
const BUCKET_MINUTES = 5;

function hourBucketIso(date = new Date()) {
  const d = new Date(date);
  const snapped = Math.floor(d.getUTCMinutes() / BUCKET_MINUTES) * BUCKET_MINUTES;
  d.setUTCMinutes(snapped, 0, 0);
  return d.toISOString();
}

/**
 * Reporter de CALIDAD (latencia + fallos) — complementario al de caídas
 * (`useConnectivityReporter`). Cada 5 minutos:
 *
 *  1. Lee el snapshot del slice `networkStatus`: `latencySamples` (últimas
 *     N llamadas reales del axios interceptor) y `consecutiveFailures`.
 *  2. Calcula avg / p95 / max sobre los samples acumulados desde el último
 *     flush. La diferencia con el snapshot anterior nos da el delta de
 *     fallos.
 *  3. POSTea al backend con el `hourBucket` actual. Si el cliente cae,
 *     pierde el batch — los fallos en sí se acumularán en el siguiente
 *     bucket cuando recupere.
 *
 * Beneficios sobre samples crudos: 1 fila por hora vs miles → manejable
 * a años. Backend hace upsert por bucket, así que reenvíos no duplican.
 */
export default function useConnectivityQualityReporter({ source = "manage-admin" } = {}) {
  const dispatch = useDispatch();
  // Snapshot del último flush para calcular deltas de fallos.
  const lastFailureCount = useRef(0);
  // Acumulador local de fallos (cuando addFailureSample dispara entre
  // ticks, llegamos al siguiente flush con el delta).
  const failuresSinceLastFlush = useRef(0);

  // El flush ahora consume el buffer de heartbeat (red PURA, sin contaminar
  // por POSTs grandes del TPV). `latencySamples` sigue alimentando el TopBar
  // y los KPIs en vivo — refleja el dolor real del usuario en ese momento.
  const heartbeatSamples = useSelector((s) => s.networkStatus?.heartbeatSamples || []);
  const consecutiveFailures = useSelector((s) => s.networkStatus?.consecutiveFailures || 0);
  const effectiveType = useSelector((s) => s.networkStatus?.effectiveType);
  // Switch global de monitorización — comparte estado con el reporter de
  // caídas y el de peticiones API. Si está OFF este hook es no-op total.
  // El heartbeat también se desactiva: no queremos pingear cuando el
  // admin ha apagado la monitorización a propósito.
  const isMonitoring = useSelector((s) => s.errors?.isMonitoring);

  // Mantenemos las refs actualizadas sin dispar el flush.
  const refHeartbeat = useRef(heartbeatSamples);
  const refFailures = useRef(consecutiveFailures);
  const refEffectiveType = useRef(effectiveType);
  const refMonitoring = useRef(isMonitoring);
  useEffect(() => { refMonitoring.current = isMonitoring; }, [isMonitoring]);
  useEffect(() => { refHeartbeat.current = heartbeatSamples; }, [heartbeatSamples]);
  useEffect(() => {
    // Si el contador subió, ese delta son fallos nuevos desde el último
    // tick. Si bajó a 0 (recuperación), no descontamos — el slice resetea
    // a 0 al recibir un sample bueno y no queremos perder los fallos
    // previos.
    const prev = refFailures.current;
    if (consecutiveFailures > prev) {
      failuresSinceLastFlush.current += (consecutiveFailures - prev);
    }
    refFailures.current = consecutiveFailures;
  }, [consecutiveFailures]);
  useEffect(() => { refEffectiveType.current = effectiveType; }, [effectiveType]);

  const flush = async () => {
    if (!refMonitoring.current) return; // switch global OFF → no-op.
    const samples = [...refHeartbeat.current];
    const failures = failuresSinceLastFlush.current;
    failuresSinceLastFlush.current = 0;

    if (samples.length === 0 && failures === 0) return;

    const sorted = [...samples].sort((a, b) => a - b);
    const avg = samples.length
      ? Math.round(samples.reduce((a, b) => a + b, 0) / samples.length)
      : 0;
    const p95 = Math.round(percentile(sorted, 95));
    const p99 = Math.round(percentile(sorted, 99));
    const max = sorted.length ? Math.round(sorted[sorted.length - 1]) : 0;

    const payload = {
      source,
      deviceId: getDeviceId(),
      hourBucket: hourBucketIso(),
      sampleCount: samples.length,
      failureCount: failures,
      avgLatencyMs: avg,
      p95LatencyMs: p95,
      p99LatencyMs: p99,
      maxLatencyMs: max,
      effectiveType: refEffectiveType.current || null,
    };

    try {
      await axios.post("/connectivity/sample", payload);
      // Éxito → vaciamos el buffer del slice para no contar las mismas
      // muestras en el próximo flush (el backend hace merge sumando
      // sampleCount; sin clear, 60s × ventana 30 = un sample contado
      // hasta 30 veces en el bucket horario).
      try { dispatch(clearHeartbeatSamples()); } catch { /* ignore */ }
    } catch {
      // Si el envío falla devolvemos los fallos al contador para que el
      // siguiente flush los reintente. Los samples crudos quedan en el
      // slice (slot rotativo) — los volveremos a enviar la próxima vez.
      failuresSinceLastFlush.current += failures;
    }
  };

  // Heartbeat — mide latencia REAL contra el backend cada 60s. Se hace
  // SIEMPRE que el switch global esté ON, independientemente de si hay
  // tráfico HTTP natural. El resultado se inyecta en el slice
  // `networkStatus` vía `addLatencySample` / `addFailureSample` — así
  // alimenta el flush de 5 min sin tocar la lógica existente, y también
  // el indicador del TopBar y los KPIs en vivo.
  //
  // NO usamos el axios global para esta llamada — el interceptor de
  // axios añadiría el ping al monitor de peticiones (ruido) y, peor,
  // re-llamaría a addLatencySample dos veces. Aquí usamos fetch nativo
  // con AbortController para timeout determinístico.
  const heartbeat = async () => {
    if (!refMonitoring.current) return; // switch OFF → no-op.
    const url = `${axios.defaults.baseURL || ""}/connectivity/ping`;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), HEARTBEAT_TIMEOUT_MS);
    const t0 = (typeof performance !== "undefined" && performance.now)
      ? performance.now() : Date.now();
    try {
      const res = await fetch(url, {
        method: "GET",
        cache: "no-store",
        signal: ctl.signal,
        // Sin credentials — el endpoint es AllowAnonymous y no necesita
        // cookies. Reduce overhead de CORS preflight.
      });
      clearTimeout(timer);
      const t1 = (typeof performance !== "undefined" && performance.now)
        ? performance.now() : Date.now();
      const ms = Math.round(t1 - t0);
      if (res && res.ok) {
        // Sample exitoso → alimenta la ventana del slice (avg/p95/max).
        try { dispatch(addHeartbeatSample(ms)); } catch { /* ignore */ }
      } else {
        // Servidor respondió con error (5xx, 4xx) → cuenta como failure
        // PERO también registramos la latencia real medida (ya respondió,
        // aunque mal). Útil para distinguir "lento" de "caído".
        try { dispatch(addHeartbeatSample(ms)); } catch { /* ignore */ }
        try { dispatch(addFailureSample()); } catch { /* ignore */ }
      }
    } catch (_) {
      clearTimeout(timer);
      // Timeout o error de red → sample alto + failure. Sin el sample
      // alto el p95/max del bucket no reflejaría el problema y el chart
      // mostraría sólo el contador de fallos.
      try { dispatch(addHeartbeatSample(HEARTBEAT_FAIL_SAMPLE_MS)); } catch { /* ignore */ }
      try { dispatch(addFailureSample()); } catch { /* ignore */ }
    }
  };

  // Refs a las funciones para que setInterval siempre llame a la versión
  // actual (capturando refMonitoring.current actualizado). Sin esto, el
  // closure del primer render queda congelado y si en el momento de
  // montaje isMonitoring era false, el interval llama una función
  // que se queda en el `if (!refMonitoring.current) return;` y nunca
  // se "despierta" cuando el switch cambia.
  const refHeartbeatFn = useRef(heartbeat);
  const refFlushFn = useRef(flush);
  useEffect(() => { refHeartbeatFn.current = heartbeat; });
  useEffect(() => { refFlushFn.current = flush; });

  useEffect(() => {
    // Ping inicial inmediato — para que un TPV recién montado ya tenga
    // un punto de medición sin esperar 60s.
    refHeartbeatFn.current && refHeartbeatFn.current();
    const hbId = setInterval(() => { refHeartbeatFn.current && refHeartbeatFn.current(); }, HEARTBEAT_INTERVAL_MS);
    const id = setInterval(() => { refFlushFn.current && refFlushFn.current(); }, FLUSH_INTERVAL_MS);
    return () => {
      clearInterval(id);
      clearInterval(hbId);
      refFlushFn.current && refFlushFn.current();
    };
  }, []); // mount-once — los refs ya cubren updates.
}
